import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import './style.scss';

interface NavItem {
    label: any;
    icon?: string;
    path: string;
    show: boolean;
    className?: string;
    end?: boolean;
    orderKey?: string;
}

interface OverflowMenuProps {
    items: NavItem[];
    rightContent?: React.ReactNode;
    orderStorageKey?: string;
}

const ORDER_STORAGE_PREFIX = 'dbmar-tab-order-';

// Tab identity for ordering, kept separate from the path so routes that carry an
// id (/hosts/123/instances) share one saved order across all records.
const orderId = (item: NavItem): string => item.orderKey || item.path;

const loadOrder = (storageKey?: string): string[] => {
    if (!storageKey) return [];
    try {
        const stored = window.localStorage.getItem(`${ORDER_STORAGE_PREFIX}${storageKey}`);
        const parsed = stored ? JSON.parse(stored) : null;
        return Array.isArray(parsed) ? parsed.filter((path: any) => typeof path === 'string') : [];
    } catch {
        return [];
    }
};

const storeOrder = (storageKey: string | undefined, order: string[]) => {
    if (!storageKey) return;
    try {
        window.localStorage.setItem(`${ORDER_STORAGE_PREFIX}${storageKey}`, JSON.stringify(order));
    } catch {
        return;
    }
};

const clearOrder = (storageKey?: string) => {
    if (!storageKey) return;
    try {
        window.localStorage.removeItem(`${ORDER_STORAGE_PREFIX}${storageKey}`);
    } catch {
        return;
    }
};

const sortByOrder = <T extends NavItem>(items: T[], order: string[]): T[] => {
    if (order.length === 0) return items;

    return items
        .map((item, index) => {
            const storedIndex = order.indexOf(orderId(item));
            return { item, rank: storedIndex === -1 ? order.length + index : storedIndex };
        })
        .sort((a, b) => a.rank - b.rank)
        .map((entry) => entry.item);
};

// The tabs a view would render, in the order the user arranged them. Views use this
// to resolve which tab to land on when opening a record.
export const getOrderedTabs = <T extends NavItem>(items: T[], storageKey?: string): T[] =>
    sortByOrder(items.filter((item) => item.show), loadOrder(storageKey));

// Keeps paths that are not currently rendered (tabs hidden for this instance type)
// at their stored position so their order is not lost.
const mergeWithStored = (paths: string[], storageKey?: string): string[] => {
    const stored = loadOrder(storageKey);
    const merged = [...paths];

    stored.forEach((path, storedIndex) => {
        if (merged.indexOf(path) !== -1) return;

        let anchor = -1;
        for (let index = storedIndex - 1; index >= 0; index--) {
            anchor = merged.indexOf(stored[index]);
            if (anchor !== -1) break;
        }
        merged.splice(anchor + 1, 0, path);
    });

    return merged;
};

export const OverflowTabbedMenu: React.FC<OverflowMenuProps> = ({ items, rightContent, orderStorageKey }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const draggedId = useRef<string | null>(null);
    const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
    const [overflowItems, setOverflowItems] = useState<NavItem[]>([]);
    const [order, setOrder] = useState<string[]>(() => loadOrder(orderStorageKey));
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);

    const reorderable = Boolean(orderStorageKey);

    const filteredItems = useMemo(() => items.filter((i) => i.show), [items]);

    const orderedItems = useMemo(() => sortByOrder(filteredItems, order), [filteredItems, order]);

    function arraysAreEqual(arr1: NavItem[], arr2: NavItem[]): boolean {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i].path !== arr2[i].path) return false;

            // Helper function to extract text content from React elements
            const getTextContent = (label: any): string => {
                if (typeof label === 'string') return label;
                if (typeof label === 'number') return label.toString();
                if (typeof label === 'object' && label !== null) {
                    // If it's a React element, try to extract text content
                    if (label.props && label.props.children) {
                        if (Array.isArray(label.props.children)) {
                            return label.props.children.map(getTextContent).join('');
                        } else {
                            return getTextContent(label.props.children);
                        }
                    }
                }
                return '';
            };

            const text1 = getTextContent(arr1[i].label);
            const text2 = getTextContent(arr2[i].label);

            if (text1 !== text2) return false;
        }
        return true;
    }

    useEffect(() => {
        if (!containerRef.current || !measureRef.current) return;

        let frameId: number | null = null;

        const update = () => {
            if (frameId) return;

            frameId = requestAnimationFrame(() => {
                frameId = null;

                if (!containerRef.current || !measureRef.current) return;

                const containerWidth = containerRef.current.offsetWidth;
                const childWidths = Array.from(measureRef.current.children).map(
                    (el) => (el as HTMLElement).offsetWidth
                );

                let total = 0;
                const visible: NavItem[] = [];
                const overflow: NavItem[] = [];

                for (let i = 0; i < orderedItems.length; i++) {
                    total += childWidths[i];
                    if (total + 75 < containerWidth) {
                        visible.push(orderedItems[i]);
                    } else {
                        overflow.push(orderedItems[i]);
                    }
                }

                setVisibleItems((prev) =>
                    arraysAreEqual(prev, visible) ? prev : visible
                );
                setOverflowItems((prev) =>
                    arraysAreEqual(prev, overflow) ? prev : overflow
                );
            });
        };

        update();

        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(containerRef.current);

        window.addEventListener("resize", update);

        return () => {
            if (frameId) cancelAnimationFrame(frameId);
            resizeObserver.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [orderedItems]);

    const moveItem = (fromId: string, toId: string) => {
        const ids = orderedItems.map(orderId);
        const fromIndex = ids.indexOf(fromId);
        const toIndex = ids.indexOf(toId);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

        ids.splice(toIndex, 0, ids.splice(fromIndex, 1)[0]);

        const merged = mergeWithStored(ids, orderStorageKey);
        setOrder(merged);
        storeOrder(orderStorageKey, merged);
    };

    const resetOrder = () => {
        clearOrder(orderStorageKey);
        setOrder([]);
    };

    const endDrag = () => {
        draggedId.current = null;
        setDraggingId(null);
        setDropTargetId(null);
    };

    const dragProps = (item: NavItem) => {
        if (!reorderable) return {};

        const id = orderId(item);

        return {
            draggable: true,
            onDragStart: (event: React.DragEvent) => {
                draggedId.current = id;
                setDraggingId(id);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', id);
            },
            onDragOver: (event: React.DragEvent) => {
                if (!draggedId.current || draggedId.current === id) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                if (dropTargetId !== id) setDropTargetId(id);
            },
            onDragLeave: () => {
                if (dropTargetId === id) setDropTargetId(null);
            },
            onDrop: (event: React.DragEvent) => {
                event.preventDefault();
                if (draggedId.current) moveItem(draggedId.current, id);
                endDrag();
            },
            onDragEnd: endDrag,
        };
    };

    const renderNavLink = (item: NavItem, dropdown?: React.ReactNode, isDraggable?: boolean) => {
        const id = orderId(item);
        const classNames = [
            isDraggable ? 'tab-reorderable' : '',
            draggingId === id ? 'tab-dragging' : '',
            dropTargetId === id ? 'tab-drop-target' : '',
        ].filter(Boolean).join(' ');

        return (
            <div key={id} className={classNames} {...(isDraggable ? dragProps(item) : {})}>
                <NavLink
                    to={item.path}
                    end={item.end}
                    draggable={false}
                    className={({ isActive }) =>
                        `${isActive ? "active" : ""} ${dropdown ? "dropdown-toggle" : ""}`
                    }
                    {...(dropdown ? { "data-bs-toggle": "dropdown", "aria-expanded": "false" } : {})}
                >
                    {item.icon && (
                        <span className="tab-icon">
                            <i className={`${item.icon} fa-fw`} aria-hidden="true" />
                            {isDraggable && (
                                <i className="fal fa-grip-dots-vertical fa-fw tab-grip" aria-hidden="true" />
                            )}
                        </span>
                    )}
                    {item.label}
                    {dropdown && <span className="badge bg-info ms-1">{overflowItems.length}</span>}
                </NavLink>
                {dropdown}
            </div>
        );
    };

    const renderMoreDropdown = () => (
        <ul className="dropdown-menu" style={{ zIndex: 1000 }}>
            {overflowItems.map((item) => (
                <li key={orderId(item)}>{renderNavLink(item, undefined, reorderable)}</li>
            ))}
            {reorderable && order.length > 0 && (
                <li>
                    <button type="button" className="tab-reset" onClick={resetOrder}>
                        <i className="fal fa-undo fa-fw" aria-hidden="true" />
                        Reset tab order
                    </button>
                </li>
            )}
        </ul>
    );

    return (
        <>
            <div
                ref={measureRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    height: 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                }}
            >
                {orderedItems.map((item) => renderNavLink(item))}
            </div>

            <div
                ref={containerRef}
                className="menu-top nav nav-tabs"
                style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    position: "relative",
                }}
            >
                {visibleItems.map((item) => renderNavLink(item, undefined, reorderable))}
                {overflowItems.length > 0 &&
                    renderNavLink(
                        {
                            label: "More",
                            path: "#",
                            show: true,
                        },
                        renderMoreDropdown()
                    )}
                {reorderable && order.length > 0 && overflowItems.length === 0 && (
                    <div className="tab-reset-inline">
                        <button type="button" onClick={resetOrder}
                                data-tooltip-id="global-tooltip"
                                data-tooltip-content="Reset tab order"
                                aria-label="Reset tab order">
                            <i className="fal fa-undo fa-fw" aria-hidden="true" />
                        </button>
                    </div>
                )}
                {rightContent && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingBottom: '3px' }}>
                        {rightContent}
                    </div>
                )}
            </div>
        </>
    );
};
